import { Component, type ErrorInfo, type ReactNode, Suspense, useMemo } from 'react';
import { DiagnosticUsageOverview } from '@gonfalon/billing/components';

import { Card, ErrorCallout } from '../../shared';
import { Box, Flex } from '../../shared/components';
import { useSettings } from '../../shared/hooks/useSettings';
import { useAccountSubscription } from '../hooks/useAccountSubscription';
import { type Account } from '../model';

import { AccountContextProvider } from './AccountContextProvider';

type DiagnosticUsageTabProps = {
  account: Account;
};

type ErrorBoundaryState = {
  hasError: boolean;
};

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Diagnostic Usage Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorCallout>
          <strong>Failed to load diagnostic usage data</strong>
          <p>Diagnostic usage data could not be loaded. Please try refreshing the page.</p>
        </ErrorCallout>
      );
    }

    return this.props.children;
  }
}

export function DiagnosticUsageTab({ account }: DiagnosticUsageTabProps) {
  const { data: subscription, isLoading, error } = useAccountSubscription(account);
  const { vitalstatistixDataExportObservability } = useSettings();

  // Create custom navigation function for vitalstatistix routing
  const detailsRedirectFn = useMemo(
    () =>
      ({ metric }: { metric: string }) => ({
        pathname: `/accounts/${account._id}/diagnostic-usage/details/${metric}`,
        search: '',
        hash: '',
      }),
    [account._id],
  );

  if (isLoading) {
    return (
      <div className="DiagnosticUsageTab">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <div>Loading diagnostic usage data...</div>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  if (error) {
    return (
      <div className="DiagnosticUsageTab">
        <Flex>
          <Box width={1} p={2}>
            <Card>
              <ErrorCallout>
                <strong>Failed to load diagnostic usage data</strong>
                <p>Diagnostic usage data could not be loaded. Please try refreshing the page.</p>
              </ErrorCallout>
            </Card>
          </Box>
        </Flex>
      </div>
    );
  }

  return (
    <div className="DiagnosticUsageTab">
      <Flex>
        <Box width={1} p={2}>
          <Card>
            <Suspense fallback={<div>Loading diagnostic usage data...</div>}>
              <ErrorBoundary>
                <AccountContextProvider
                  key={String(vitalstatistixDataExportObservability)}
                  account={account}
                  subscription={subscription || undefined}
                >
                  <DiagnosticUsageOverview detailsRedirectFn={detailsRedirectFn} />
                </AccountContextProvider>
              </ErrorBoundary>
            </Suspense>
          </Card>
        </Box>
      </Flex>
    </div>
  );
}
